/**
 * Shared plumbing for the Environnement tab's data blocks (DPE, écoles,
 * commerces, transports, ARCEP, …).
 *
 * Every block follows the same lifecycle :
 *   1. Read `/users/{uid}/fiches/{ficheId}/env/{blockId}` from Firestore.
 *   2. If the cached doc exists, is status="ready", and its `inputsKey`
 *      matches the current one (same lat/lng rounded, same INSEE, …) AND
 *      is within TTL → serve it, done.
 *   3. Otherwise fetch the source API client-side, write a new cache doc,
 *      return the fresh data.
 *
 * Why a subcollection rather than a subfield on the fiche ?
 *   - Independent read/write cadence per block (the user can flip fibre on
 *     without touching écoles).
 *   - Per-block `pdfInclude` flag (opt-in per carte for the future PDF
 *     export — decision logged 2026-04-22).
 *   - Future-proofs the server-side writers (Cloud Functions can rewrite
 *     one block without racing the client on the parent fiche doc).
 *
 * TTL strategy : écoles/commerces/ARCEP only move on the order of
 * months, so we refetch roughly once a month. DPE moves faster (new
 * diagnostics daily) but cached data is still usable — we re-fetch on
 * open if older than 14 days. The per-block hook picks its own TTL.
 */

import { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
  type FieldValue,
} from "firebase/firestore";
import { db, auth } from "../firebase";

export type EnvBlockId =
  | "ecoles"
  | "commerces"
  | "dpe"
  | "transports"
  | "arcep"
  | "priceHistory"
  | "bruit"
  | "abf"
  | "filosofi"
  | "loyers"
  | "permits";

export type EnvBlockDoc<T> = {
  status: "ready" | "error";
  fetchedAt: Timestamp | FieldValue | null;
  // Snapshot of the inputs used to derive this block. If the current
  // inputs differ, we refetch. `lat,lng` are rounded to 3 decimals
  // (~110 m), which is finer than the underlying data sources.
  inputsKey: string;
  // Opt-in flag for the future PDF export (default off).
  pdfInclude: boolean;
  // Source-specific payload. Opaque to the shared infra.
  data: T;
  // Populated only when status === "error" — retained so the UI can show
  // a fallback message without blanking the card.
  errorMessage?: string;
};

export function envBlockRef(
  uid: string,
  ficheId: string,
  blockId: EnvBlockId,
) {
  return doc(db, "users", uid, "fiches", ficheId, "env", blockId);
}

/**
 * Stable 3-decimal rounding for lat/lng inputs.
 *
 * 3 decimals → ~110 m granularity. Two fiches within a block cache-share
 * when they're in the same ~100 m square — good enough for écoles /
 * commerces / DPE (all 500–1500 m radii), and avoids refetching on
 * sub-parcel jitter coming from BAN.
 */
export function coordKey(lat: number, lng: number): string {
  return `${lat.toFixed(3)},${lng.toFixed(3)}`;
}

// ─── In-flight dedup ────────────────────────────────────────────────────

/**
 * When a fiche has both a detail card (`EcolesCard`) and a synthesis bar
 * summarising the same block, two `useEnvBlock` hooks mount concurrently —
 * without coordination they'd each hit Firestore, each call the upstream
 * API on a miss, and each write the cache. This module-level map dedupes
 * the work: the first hook to resolve a key wins, the second awaits the
 * same promise and gets the same result.
 *
 * Keyed on (uid | ficheId | blockId | inputsKey) so a parcel edit or a
 * user switch cleanly evicts.
 */
type InflightResult<T> =
  | { kind: "ready"; data: T; fromCache: boolean; fetchedAt: Date | null }
  | { kind: "error"; message: string };
const inflightBlocks = new Map<string, Promise<InflightResult<unknown>>>();

function inflightKey(
  uid: string, ficheId: string, blockId: EnvBlockId, inputsKey: string,
): string {
  return `${uid}|${ficheId}|${blockId}|${inputsKey}`;
}

// ─── Hook ────────────────────────────────────────────────────────────────

type State<T> =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: T; fromCache: boolean; fetchedAt: Date | null }
  | { status: "error"; message: string };

type EnvBlockOptions<T> = {
  ficheId: string;
  blockId: EnvBlockId;
  inputsKey: string;
  // Number of days after which we consider the cache stale.
  ttlDays: number;
  // The actual API-call body — only invoked on miss / stale.
  fetcher: () => Promise<T>;
};

/**
 * `useEnvBlock` — cache-first loader for a single Environnement block.
 *
 * The hook is idempotent per (ficheId, blockId, inputsKey) : two cards
 * asking for the same block on the same fiche still each make their own
 * Firestore read, but the refetch branch is guarded by the cache doc so
 * we don't double-call the upstream API even in StrictMode double-effects.
 */
export function useEnvBlock<T>(opts: EnvBlockOptions<T> | null): State<T> {
  const [state, setState] = useState<State<T>>({ status: "idle" });

  useEffect(() => {
    if (!opts) {
      setState({ status: "idle" });
      return;
    }
    const { ficheId, blockId, inputsKey, ttlDays, fetcher } = opts;
    const user = auth.currentUser;
    if (!user) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    const key = inflightKey(user.uid, ficheId, blockId, inputsKey);

    // If another hook already kicked off the same work in this session,
    // latch onto its promise so we share the cache read / API call / write.
    let promise = inflightBlocks.get(key) as
      | Promise<InflightResult<T>>
      | undefined;

    if (!promise) {
      promise = (async (): Promise<InflightResult<T>> => {
        const ref = envBlockRef(user.uid, ficheId, blockId);

        // 1. Cache read
        try {
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const cached = snap.data() as EnvBlockDoc<T>;
            const fresh = isFresh(cached, ttlDays);
            const keyMatches = cached.inputsKey === inputsKey;
            if (cached.status === "ready" && fresh && keyMatches) {
              return {
                kind: "ready",
                data: cached.data,
                fromCache: true,
                fetchedAt: toDate(cached.fetchedAt),
              };
            }
          }
        } catch (e) {
          console.warn(`[env/${blockId}] cache read failed`, e);
        }

        // 2. Fetch fresh
        try {
          const data = await fetcher();

          // 3. Cache write (fire-and-forget — don't block the UI on it)
          const payload: EnvBlockDoc<T> = {
            status: "ready",
            fetchedAt: serverTimestamp(),
            inputsKey,
            pdfInclude: false,
            data,
          };
          void setDoc(ref, payload).catch((e) => {
            console.warn(`[env/${blockId}] cache write failed`, e);
          });

          return {
            kind: "ready",
            data,
            fromCache: false,
            fetchedAt: new Date(),
          };
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          // Persist the error briefly so retries don't hammer the API on
          // every re-mount.
          void setDoc(ref, {
            status: "error",
            fetchedAt: serverTimestamp(),
            inputsKey,
            pdfInclude: false,
            data: null,
            errorMessage: message,
          } satisfies EnvBlockDoc<null>).catch(() => {});
          return { kind: "error", message };
        }
      })();

      // Store the promise as a generic so we can drop it after settle —
      // any hook racing us here sees the same promise.
      inflightBlocks.set(
        key,
        promise as unknown as Promise<InflightResult<unknown>>,
      );
      // Drop the in-flight entry once the work has settled so a future
      // mount (e.g. after navigating away and back in the same tab) still
      // benefits from the normal Firestore cache path.
      void promise.finally(() => {
        if (inflightBlocks.get(key) === (promise as unknown)) {
          inflightBlocks.delete(key);
        }
      });
    }

    void promise.then((result) => {
      if (cancelled) return;
      if (result.kind === "ready") {
        setState({
          status: "ready",
          data: result.data,
          fromCache: result.fromCache,
          fetchedAt: result.fetchedAt,
        });
      } else {
        setState({ status: "error", message: result.message });
      }
    });

    return () => {
      cancelled = true;
    };
    // We key the effect on inputsKey so a parcel edit triggers a refetch.
  }, [opts?.ficheId, opts?.blockId, opts?.inputsKey, opts?.ttlDays]);

  return state;
}

// ─── Small helpers ──────────────────────────────────────────────────────

function isFresh(
  cached: EnvBlockDoc<unknown>,
  ttlDays: number,
): boolean {
  const fetchedAt = toDate(cached.fetchedAt);
  if (!fetchedAt) return false;
  const ageMs = Date.now() - fetchedAt.getTime();
  return ageMs < ttlDays * 24 * 3600 * 1000;
}

function toDate(ts: Timestamp | FieldValue | null | undefined): Date | null {
  if (!ts) return null;
  if (ts instanceof Timestamp) return ts.toDate();
  // ServerTimestamp placeholder hasn't been materialized yet (e.g. called
  // right after the local optimistic write). Treat as "now".
  return new Date();
}

// ─── Geodistance helper (Haversine, meters) ─────────────────────────────

/**
 * Great-circle distance between two lat/lng points, in meters.
 * Shared by the per-source clients that need "nearest / within radius"
 * logic (écoles, commerces, transports, DPE). ~0.5 % error at the scales
 * we care about (< 5 km).
 */
export function haversineMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
